import React, { useState, useEffect } from "react";
import { getFirestore, query, collection, getDocs } from "firebase/firestore";
import app from "../firebaseConfig";
import VehicleCard from "./VehicleCard";
import Slider from "@mui/material/Slider";
import SearchIcon from "@mui/icons-material/Search";

// Import mobile-only components from your existing project pages:

import HowItWorksPage from "./HowItWorksPage";
import WhyCarsPage from "./WhyCarsPage";
import AboutPage from "./AboutPage";

// Import the background image (adjust path if necessary)
import back7 from "./back8.png";

const db = getFirestore(app);

const MobileCarListingPage = () => {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);

  // Filters object
  const [filters, setFilters] = useState({
    carType: "",
    valueRange: [0, 1000000],
    roiRange: [0, 50],
  });

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const q = query(collection(db, "cars"));
        const snap = await getDocs(q);
        const fetched = snap.docs.map((doc) => ({
          carID: doc.id,
          ...doc.data(),
        }));

        setCars(fetched);
        // Immediately apply filters to ensure fully-funded cars go to the bottom
        const initialFiltered = applyAllFilters(fetched, filters, "");
        setFilteredCars(initialFiltered);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cars:", error);
        setLoading(false);
      }
    };
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const separateFullyFunded = (list) => {
    const notFullyFunded = [];
    const fullyFunded = [];

    list.forEach((car) => {
      const moneyRaised = Number(car.moneyRaised) || 0;
      const totalValue = Number(car.totalValue) || 1;
      const progress = (moneyRaised / totalValue) * 100;

      if (progress >= 100) {
        fullyFunded.push(car);
      } else {
        notFullyFunded.push(car);
      }
    });

    return [...notFullyFunded, ...fullyFunded];
  };

  const applyAllFilters = (carsList, filterObject, searchText) => {
    let temp = [...carsList];

    // 1. Search by carName (case-insensitive)
    const searchTrim = searchText.trim().toLowerCase();
    if (searchTrim) {
      temp = temp.filter((car) =>
        (car.carName || "").toLowerCase().includes(searchTrim)
      );
    }

    // 2. Car Type filter (exact match)
    if (filterObject.carType.trim()) {
      const desiredType = filterObject.carType.toLowerCase();
      temp = temp.filter((car) => {
        const ctype = (car.carType || "").toLowerCase().trim();
        return ctype === desiredType;
      });
    }

    // 3. Car Value Range
    const [minVal, maxVal] = filterObject.valueRange;
    temp = temp.filter((car) => {
      const val = Number(car.totalValue) || 0;
      return val >= minVal && val <= maxVal;
    });

    // 4. ROI Range
    const [minROI, maxROI] = filterObject.roiRange;
    temp = temp.filter((car) => {
      const roi = Number(car.averageROI) || 0;
      return roi >= minROI && roi <= maxROI;
    });

    // 5. Sort fully-funded cars to the bottom
    const finalList = separateFullyFunded(temp);
    return finalList;
  };

  const handleSearch = (e) => {
    const newSearch = e.target.value;
    setSearch(newSearch);
    const finalList = applyAllFilters(cars, filters, newSearch);
    setFilteredCars(finalList);
  };

  const handleFilterChange = (key, newValue) => {
    const updated = { ...filters, [key]: newValue };
    setFilters(updated);
    const finalList = applyAllFilters(cars, updated, search);
    setFilteredCars(finalList);
  };

  const handleROISliderChange = (event, newValue) => {
    handleFilterChange("roiRange", newValue);
  };

  const handleValueSliderChange = (event, newValue) => {
    handleFilterChange("valueRange", newValue);
  };

  return (
    <div
      style={{
        backgroundImage: `url(${back7})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        margin: 0,
        padding: "10px",
        fontFamily: "Arial, sans-serif",
        color: "#fff",
      }}
    >
      {/* Top Sign Up Section */}

      {/* Search & Filter Section */}
      <div
        style={{
          marginBottom: "15px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "400px",
          margin: "15px auto",
        }}
      >
        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder="Find your next investment vehicle..."
          style={{
            flexGrow: 1,
            padding: "10px",
            fontSize: "1rem",
            border: "1px solid #ccc",
            borderRadius: "5px 0 0 5px",
            outline: "none",
          }}
        />
        <button
          onClick={() => setShowFilter(!showFilter)}
          style={{
            padding: "10px",
            border: "1px solid #ccc",
            borderLeft: "none",
            borderRadius: "0 5px 5px 0",
            backgroundColor: "#007bff",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          <SearchIcon fontSize="small" />
        </button>
      </div>

      {/* Filter Box */}
      {showFilter && (
        <div
          style={{
            backgroundColor: "#1e1e1e",
            padding: "10px",
            borderRadius: "5px",
            marginBottom: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            alignItems: "center",
          }}
        >
          <h3 style={{ color: "white", margin: 0 }}>Filters</h3>

          {/* Car Type Dropdown */}
          <select
            name="carType"
            value={filters.carType}
            onChange={(e) => handleFilterChange("carType", e.target.value)}
            style={{
              padding: "8px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              width: "90%",
              maxWidth: "300px",
            }}
          >
            <option value="">All</option>
            <option value="commuter vehicle">Commuter Vehicle</option>
            <option value="supercar">SuperCar</option>
            <option value="hypercar">HyperCar</option>
            <option value="truck">Truck</option>
            <option value="rv">RV</option>
            <option value="special vehicle">Special Vehicle</option>
          </select>

          {/* Car Value Range */}
          <div style={{ width: "90%", maxWidth: "300px" }}>
            <label style={{ fontSize: "0.9rem", color: "#fff" }}>Car Value</label>
            <Slider
              value={filters.valueRange}
              onChange={handleValueSliderChange}
              valueLabelDisplay="auto"
              min={0}
              max={1000000}
              style={{ color: "#32B704" }}
            />
            <p style={{ fontSize: "0.8rem", textAlign: "center" }}>
              ${filters.valueRange[0].toLocaleString()} - ${filters.valueRange[1].toLocaleString()}
            </p>
          </div>

          {/* ROI Range */}
          <div style={{ width: "90%", maxWidth: "300px" }}>
            <label style={{ fontSize: "0.9rem", color: "#fff" }}>ROI</label>
            <Slider
              value={filters.roiRange}
              onChangeCommitted={handleROISliderChange}
              onChange={(e, newValue) => {
                setFilters((prev) => ({ ...prev, roiRange: newValue }));
              }}
              valueLabelDisplay="auto"
              min={0}
              max={50}
              style={{ color: "#32B704" }}
            />
            <p style={{ fontSize: "0.8rem", textAlign: "center" }}>
              {filters.roiRange[0]}% - {filters.roiRange[1]}%
            </p>
          </div>
        </div>
      )}

      {/* Vehicles List - Centered */}
      {loading ? (
        <p style={{ textAlign: "center" }}>Loading vehicles...</p>
      ) : filteredCars.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            margin: "20px 0",
            alignItems: "center",
          }}
        >
          {filteredCars.map((car) => {
            const moneyRaised = Number(car.moneyRaised) || 0;
            const totalValue = Number(car.totalValue) || 1;
            const progress = (moneyRaised / totalValue) * 100;
            return (
              <VehicleCard
                key={car.carID}
                carID={car.carID}
                carName={car.carName}
                tags={car.tags || []}
                image={
                  car.pictures
                    ? car.pictures[0]
                    : "https://via.placeholder.com/300"
                }
                totalReceived={car.moneyRaised}
                roi={car.averageROI}
                totalValue={car.totalValue}
                location={car.location}
                progress={progress}
              />
            );
          })}
        </div>
      ) : (
        <p style={{ textAlign: "center", color: "#777" }}>
          No vehicles match your search.
        </p>
      )}

      {/* How It Works Section */}
      <div style={{ margin: "30px 0" }}>
        <HowItWorksPage />
      </div>

      {/* Combined Information Sections: Why Cars and About */}
      <div style={{ margin: "30px 0" }}>
        <WhyCarsPage />
        <AboutPage />
      </div>

      {/* Bottom Sign Up Section */}
   
    </div>
  );
};

export default MobileCarListingPage; 